import { DEFAULT_SEAT_PLANS, LEGACY_PLANS, NEW_PLANS, PLANS, PLANS_ORDER } from './constants';
import { PlanData } from './interface';

export type LateStagePaywallKind =
  | 'legacy'
  | 'limit_based_bsp'
  | 'limits_only_bsp'
  | 'bsp_cards_generic_and_bsp_limits'
  | 'bsp_table_generic_and_bsp_limits';
export type LateStageTierSetting = 'one_up' | 'standard' | 'advanced' | 'creator' | 'professional';
export type LateStageVariant = 'storage' | 'privacy' | 'generic';
type LateStageTierFamily = 'default' | 'legacy' | 'repackaging';
export type LateStagePrivacyOption = 'unlisted' | 'password' | 'hide_from_vimeo';

type ResolveTierArgs = {
  currentTier: PLANS;
  tierSetting: LateStageTierSetting;
  plansData: PlanData[];
  excludePlans?: Array<PLANS>;
  variant?: LateStageVariant;
};

const CREATOR_V2_PRODUCT_IDS = ['01KGPR56B56Z9H89TBK6NENWDQ', '01KGPR56B56Z9H89TBK87BF9BS'];

const LATE_STAGE_SUPPORTED_TIERS = new Set<PLANS>([
  ...DEFAULT_SEAT_PLANS.filter((tier) => tier !== PLANS.ENTERPRISE),
  ...LEGACY_PLANS.filter((tier) => tier !== PLANS.ENTERPRISE),
  PLANS.FREE,
  ...NEW_PLANS,
]);

const TIER_SETTING_TARGETS: Record<
  Exclude<LateStageTierSetting, 'one_up'>,
  Record<LateStageTierFamily, PLANS>
> = {
  standard: {
    default: PLANS.STANDARD,
    legacy: PLANS.PRO,
    repackaging: PLANS.PROFESSIONAL,
  },
  advanced: {
    default: PLANS.ADVANCED,
    legacy: PLANS.BUSINESS,
    repackaging: PLANS.PROFESSIONAL,
  },
  creator: {
    default: PLANS.STARTER,
    legacy: PLANS.PLUS,
    repackaging: PLANS.CREATOR,
  },
  professional: {
    default: PLANS.ADVANCED,
    legacy: PLANS.BUSINESS,
    repackaging: PLANS.PROFESSIONAL,
  },
};

const BSP_LATE_STAGE_PAYWALL_KINDS = new Set<LateStagePaywallKind>([
  'limit_based_bsp',
  'limits_only_bsp',
  'bsp_cards_generic_and_bsp_limits',
  'bsp_table_generic_and_bsp_limits',
]);

export const isBspLateStagePaywallKind = (kind: LateStagePaywallKind) =>
  BSP_LATE_STAGE_PAYWALL_KINDS.has(kind);

export const resolveVariant = (paywallFeature?: string): LateStageVariant => {
  if (paywallFeature === 'storage_limit') {
    return 'storage';
  }

  if (paywallFeature === 'privacy' || paywallFeature === 'showcase_privacy') {
    return 'privacy';
  }

  return 'generic';
};

export const resolvePrivacyOptionFromTrigger = (
  paywallTrigger?: string,
): LateStagePrivacyOption | null => {
  const normalizedTrigger = paywallTrigger?.toLowerCase();

  if (!normalizedTrigger) {
    return null;
  }

  if (normalizedTrigger.includes('unlisted_privacy')) {
    return 'unlisted';
  }

  if (normalizedTrigger.includes('disable_privacy')) {
    return 'hide_from_vimeo';
  }

  if (normalizedTrigger.includes('password_privacy')) {
    return 'password';
  }

  return null;
};

const isHigherTier = (tier: PLANS, currentTier: PLANS) => {
  return (PLANS_ORDER[tier] ?? -1) > (PLANS_ORDER[currentTier] ?? -1);
};

const hasCreatorV2Plan = (plansData: PlanData[]) => {
  const creatorPlan = plansData.find((plan) => plan.tier === PLANS.CREATOR);

  return (
    creatorPlan !== undefined &&
    (CREATOR_V2_PRODUCT_IDS.includes(creatorPlan.id?.monthly ?? '') ||
      CREATOR_V2_PRODUCT_IDS.includes(creatorPlan.id?.annual ?? ''))
  );
};

const getOneUpTier = (currentTier: PLANS, plansData: PlanData[]): PLANS | null => {
  return (
    plansData
      .filter((plan) => isHigherTier(plan.tier, currentTier))
      .sort((a, b) => (PLANS_ORDER[a.tier] ?? 0) - (PLANS_ORDER[b.tier] ?? 0))[0]?.tier ?? null
  );
};

const isLateStageTier = (tier: PLANS | null) => {
  return tier !== null && LATE_STAGE_SUPPORTED_TIERS.has(tier);
};

const resolveTierFamily = (plansData: PlanData[]): LateStageTierFamily => {
  const availableTiers = new Set<PLANS>(plansData.map((plan) => plan.tier));

  if (availableTiers.has(PLANS.PRODUCTION) || availableTiers.has(PLANS.PROFESSIONAL)) {
    return 'repackaging';
  }

  if (availableTiers.has(PLANS.PREMIUM)) {
    return 'legacy';
  }

  return 'default';
};

export const resolveTier = ({
  currentTier,
  tierSetting,
  plansData,
  excludePlans = [],
  variant,
}: ResolveTierArgs): PLANS | null => {
  const effectiveExcludePlans =
    variant === 'privacy' && hasCreatorV2Plan(plansData)
      ? [...excludePlans, PLANS.CREATOR]
      : excludePlans;
  const filteredPlansData = plansData.filter((plan) => !effectiveExcludePlans.includes(plan.tier));
  const oneUpTier = getOneUpTier(currentTier, filteredPlansData);
  const oneUpLateStageTier = isLateStageTier(oneUpTier) ? oneUpTier : null;

  if (tierSetting === 'one_up') {
    return oneUpLateStageTier;
  }

  const tierFamily = resolveTierFamily(plansData);
  const targetTier = TIER_SETTING_TARGETS[tierSetting][tierFamily];

  // If user is already on target tier or above, fallback to one_up.
  if ((PLANS_ORDER[currentTier] ?? -1) >= (PLANS_ORDER[targetTier] ?? -1)) {
    return oneUpLateStageTier;
  }

  // If target tier is available and above current tier, use it; otherwise fallback to one_up.
  const hasTargetTier = filteredPlansData.some(
    (plan) => plan.tier === targetTier && isHigherTier(plan.tier, currentTier),
  );
  return hasTargetTier ? targetTier : oneUpLateStageTier;
};
